import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import Dialog from '@mui/material/Dialog';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import FormControl from '@mui/material/FormControl';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import Checkbox from 'src/components/input/checkbox/Checkbox';
import type { ILead } from 'src/services/api';
import type { IShowing } from 'src/api/landlord-showings-api';
import {
    type ICreateShowingRequest,
    useCalendarIntegrations,
    useContacts,
    useCreateShowing,
    useProperties,
    useShowings,
    useUpdateShowing,
    useTouringHours,
    formatAvailabilitySummary,
    getBuildingLocalDateTime,
    getDayAvailability,
    isBuildingLocalDateTimeWithinAvailability,
    resolvePropertyContactAssignments,
} from 'src/services/api';
import { useProperty } from 'src/services/api/properties/use-property';
import { useUpdateLead } from 'src/services/api/leads/use-update-lead';
import { useTeamMembers } from 'src/services/api/team/members/use-team-members';
import { useAuthenticatedState } from 'src/authenticated-state/context';
import { Flex, FlexColumn } from 'src/components/flex';
import { Transition } from 'src/components/ModalTransition';
import ReffieLoadingButton from 'src/components/reffie-loading-button/ReffieLoadingButton';
import {
    collectBuildingPropertyIds,
    exceedsTourConcurrency,
} from 'src/components/create-or-update-tour-modal/tour-concurrency';
import {
    formatDateTimeForApi,
    getDefaultTourDateTime,
    getEffectiveTourTimezone,
    getTimezoneAbbr,
} from 'src/utils/format-date-time';
import { EFeatureFlag, useIsFeatureEnabled } from 'src/utils/feature-flags';
import EliseSlotGrid from 'src/components/create-or-update-elise-tour-modal/EliseSlotGrid';
import { useEliseTourSlotSelection } from 'src/components/create-or-update-elise-tour-modal/use-elise-tour-slot-selection';

import styles from 'src/components/create-or-update-tour-modal/CreateOrUpdateTourModal.module.css';

const ELISE_AI_PROVIDER = 'elise_ai';

interface Props {
    open: boolean;
    onClose: () => void;
    /**
     * The lead that is initially selected
     * Do not pass anything (undefined) if none are selected
     */
    selectedLeadUuid?: string;
    /**
     * Necessary to edit the showing
     */
    landlordUserId?: number;
    /**
     * Array of leads to choose from
     * Necessary for the user to be able to switch between leads
     * If you do not want the user to switch between leads, simply pass a single lead
     */
    leads: ILead[];
    /**
     * Houses both the date and time
     */
    initDateTime?: string;
    initPropertyId: number | null;
    /**
     * If this is an existing showing, this will be the ID of the showing
     */
    showingId?: number;
    /**
     * If this showing was created from message contents, this is the UUID of the message
     */
    sourceMessageUuid?: string;
    onCreated?: (showing: ICreateShowingRequest) => void;
    isSelfShow?: boolean;
    /**
     * Called whenever the selected property changes (including the initial
     * selection). Lets a host calendar follow along so its touring-hours
     * availability matches the property being booked.
     */
    onChanged?: (propertyId: number | null) => void;
}

export default function CreateOrUpdateTourModal(props: Props) {
    const { data: properties } = useProperties();
    const { data: teamMembers } = useTeamMembers();
    const { loggedInUser, userSettings } = useAuthenticatedState();

    const initSelectedLead = props.leads.find((lead) => lead.uuid === props.selectedLeadUuid) || null;
    // The picker holds a bare wall-clock; the effective timezone is applied only
    // at submit. `initDateTime` is a zone-less wall-clock string — every entry
    // point passes exactly the time it means — so it is shown verbatim.
    const initDateVal = props.initDateTime ?
        dayjs(props.initDateTime) :
        getDefaultTourDateTime();

    const [lead, setLead] = useState<{ value: string, label: string | null } | null>(
        initSelectedLead ? { value: initSelectedLead.uuid, label: initSelectedLead.name } : null
    );
    const [isSelfShow, setIsSelfShow] = useState<boolean>(props.isSelfShow || false);
    const [property, setProperty] = useState<{ value: number, label: string } | null>();
    const [date, setDate] = useState<dayjs.Dayjs | null>(initDateVal);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [eliseSelectedSlot, setEliseSelectedSlot] = useState<string | null>(null);
    const { mutateAsync: createShowing, isLoading: isCreateLoading } = useCreateShowing();
    const { mutateAsync: updateShowing, isLoading: isUpdateLoading } = useUpdateShowing();
    const { mutateAsync: updateLead, isLoading: isTransferring } = useUpdateLead(lead?.value ?? '');

    const isSaveOrUpdateLoading = isCreateLoading || isUpdateLoading;

    const selectedLead = props.leads.find(l => l.uuid === lead?.value) ?? null;
    const owner = teamMembers?.find(m => m.id === selectedLead?.owner) ?? null;
    const ownerName = owner?.name ?? 'another team member';
    const isOwner = !selectedLead || selectedLead.owner === loggedInUser.id;

    const { data: calendarIntegrations } = useCalendarIntegrations();
    const { data: contacts } = useContacts();
    const hasCalendarIntegration = Boolean(
        calendarIntegrations?.google || calendarIntegrations?.outlook
    );
    const leadHasEmail = Boolean(selectedLead?.email);

    const isAutoInviteEnabled = userSettings.is_auto_invite_lead_to_tour;
    const senderEmail = owner?.email ?? null;
    const enabledPropertyContacts = useMemo(() => {
        if (property?.value == null || !contacts || !properties) {
            return [];
        }
        return resolvePropertyContactAssignments(contacts, properties, property.value)
            .filter(({ enabled }) => enabled)
            .map(({ contact }) => contact);
    }, [contacts, properties, property?.value]);
    const inviteRecipientEmails = useMemo(() => {
        if (!isAutoInviteEnabled || !selectedLead?.email || !senderEmail) {
            return [];
        }

        const seen = new Set<string>([senderEmail]);
        const emails: string[] = [];
        [selectedLead.email, ...enabledPropertyContacts.map((c) => c.email)]
            .forEach((email) => {
                if (email && !seen.has(email)) {
                    seen.add(email);
                    emails.push(email);
                }
            });
        return emails;
    }, [isAutoInviteEnabled, selectedLead?.email, senderEmail, enabledPropertyContacts]);
    const willSendInvite = hasCalendarIntegration && inviteRecipientEmails.length > 0;

    const isEliseIntegrationEnabled = useIsFeatureEnabled(EFeatureFlag.ELISE_AI_INTEGRATION);
    const { data: selectedProperty, isLoading: isSelectedPropertyLoading } = useProperty(
        isEliseIntegrationEnabled ? property?.value : null,
        { include_integrations: true },
    );
    const eliseIntegration = selectedProperty?.integrations?.integrations
        .find((i) => i.provider === ELISE_AI_PROVIDER);

    const isElise = isEliseIntegrationEnabled && !!eliseIntegration && !!selectedProperty;

    const eliseSlotSelection = useEliseTourSlotSelection({
        open: props.open && isElise,
        eliseBuildingId: eliseIntegration?.external_property_id ?? undefined,
        initialDate: date,
    });

    const { data: touringHours } = useTouringHours(property?.value);
    const touringHoursTimezone = touringHours?.timezone;
    // The tour is created and submitted in the building's timezone when it has
    // one, otherwise the user's.
    const { timezone: effectiveTourTimezone, isBuilding: isBuildingTimezone } =
        getEffectiveTourTimezone(touringHoursTimezone, userSettings);
    const effectiveTimezoneLabel = getTimezoneAbbr(effectiveTourTimezone);
    const isDateInPast = useMemo(
        () => !!date && dayjs(formatDateTimeForApi(date, effectiveTourTimezone)).isBefore(dayjs()),
        [date, effectiveTourTimezone]
    );
    const minPickerDateTime = dayjs(
        dayjs().tz(effectiveTourTimezone).format('YYYY-MM-DDTHH:mm:ss')
    );
    const buildingLocalDate = useMemo(() => {
        if (!date || !touringHoursTimezone) {
            return null;
        }
        return getBuildingLocalDateTime(date.toDate(), effectiveTourTimezone, touringHoursTimezone);
    }, [date, effectiveTourTimezone, touringHoursTimezone]);
    const selectedDayAvailability = useMemo(() => {
        if (!touringHours?.is_set || !buildingLocalDate) {
            return undefined;
        }
        return getDayAvailability(touringHours, buildingLocalDate.format('YYYY-MM-DD'));
    }, [buildingLocalDate, touringHours]);
    const isOutsideTouringHours = Boolean(
        touringHours?.is_set &&
        buildingLocalDate &&
        !isBuildingLocalDateTimeWithinAvailability(buildingLocalDate, selectedDayAvailability)
    );

    // Concurrency is enforced per building across all its units, rooted at the
    // effective building and falling back to the property itself.
    const maxConcurrentTours = touringHours?.max_concurrent_tours ?? null;
    const buildingRootId = touringHours?.source_property_id ?? property?.value ?? null;
    const buildingPropertyIds = buildingRootId !== null && properties ?
        collectBuildingPropertyIds(properties, buildingRootId) :
        new Set<number>();

    const slotStart = date ?? null;
    const { data: sameDayTours } = useShowings({
        starts_after: slotStart ? slotStart.startOf('day').format('YYYY-MM-DDTHH:mm:ss') : '',
        starts_before: slotStart ? slotStart.endOf('day').format('YYYY-MM-DDTHH:mm:ss') : undefined,
        enabled: Boolean(slotStart && maxConcurrentTours),
    });

    const exceedsConcurrency = maxConcurrentTours && slotStart && sameDayTours ?
        exceedsTourConcurrency({
            maxConcurrentTours,
            // The picker holds a bare wall-clock; anchor it to the effective
            // timezone so its absolute instant lines up with each tour's
            // start_time (also an absolute instant) when checking overlap.
            slotStartMs: dayjs(formatDateTimeForApi(slotStart, effectiveTourTimezone)).valueOf(),
            tours: sameDayTours,
            buildingPropertyIds,
            editingTourId: props.showingId,
        }) :
        false;

    const handleCreate = async () => {
        if (!lead) {
            setErrorMessage('please select a lead');
            return;
        }
        if (!property) {
            setErrorMessage('please select a property');
            return;
        }
        if (isElise) {
            if (!eliseSelectedSlot) {
                setErrorMessage('please select a tour time');
                return;
            }
        } else if (!date) {
            setErrorMessage('please select a date');
            return;
        }
        const payload: ICreateShowingRequest = isElise ? {
            property_id: property.value,
            lead_uuid: lead.value,
            start_time: eliseSelectedSlot as string,
            timezone: eliseSlotSelection.eliseTimezone ?? dayjs.tz.guess(),
            source_message_uuid: props.sourceMessageUuid || null,
            is_self_show: false,
        } : {
            property_id: property?.value,
            lead_uuid: lead.value,
            start_time: formatDateTimeForApi(date as dayjs.Dayjs, effectiveTourTimezone),
            timezone: effectiveTourTimezone,
            source_message_uuid: props.sourceMessageUuid || null,
            is_self_show: isSelfShow,
        };
        await createShowing(payload);
        props.onClose();
        props?.onCreated?.(payload);
    };

    const handleTransferOwnership = async () => {
        if (!loggedInUser) { return; }
        await updateLead({ owner: loggedInUser.id });
    };

    const handleEdit = async () => {
        if (!property) {
            setErrorMessage('please select a property');
            return;
        }
        if (isElise) {
            if (!eliseSelectedSlot) {
                setErrorMessage('please select a tour time');
                return;
            }
        } else if (!date) {
            setErrorMessage('please select a date');
            return;
        }

        const updatedShowing = isElise ? {
            id: props.showingId,
            landlord_user_id: props.landlordUserId,
            property_id: property.value,
            lead_uuid: lead?.value,
            start_time: eliseSelectedSlot,
            timezone: eliseSlotSelection.eliseTimezone ?? dayjs.tz.guess(),
            source_message_uuid: props.sourceMessageUuid || null,
            is_self_show: isSelfShow,
        } as IShowing : {
            id: props.showingId,
            landlord_user_id: props.landlordUserId,
            property_id: property.value,
            lead_uuid: lead?.value,
            start_time: formatDateTimeForApi(date as dayjs.Dayjs, effectiveTourTimezone),
            timezone: effectiveTourTimezone,
            source_message_uuid: props.sourceMessageUuid || null,
            is_self_show: isSelfShow,
        } as IShowing;

        await updateShowing(updatedShowing);
        props.onClose();
    };

    const handleSave = async () => {
        if (props.showingId) {
            await handleEdit();
        } else {
            await handleCreate();
        }
    };

    const leadOptions = useMemo(() => {
        return props.leads.map((o) => {
            if (!o.name) {
                return {
                    value: o.uuid,
                    label: 'Unknown Lead',
                };
            }
            return {
                value: o.uuid,
                label: o.name,
            };
        });
    }, [props.leads]);

    const propertyOptions = useMemo(() => {
        if (!properties) {
            return [];
        }

        return properties.flatMap((propertyOption) => propertyOption ? [{
            value: propertyOption.id,
            label: propertyOption.name,
        }] : []);
    }, [properties]);

    useEffect(() => {
        const leadProperty = propertyOptions?.find((o) => o.value === props.initPropertyId);
        setProperty(prev => {
            if (prev === undefined) {
                return leadProperty;
            } else {
                return prev;
            }
        });
    }, [props.leads, propertyOptions, props.initPropertyId]);

    const { onChanged } = props;
    useEffect(() => {
        if (property !== undefined) {
            onChanged?.(property?.value ?? null);
        }
    }, [property, onChanged]);

    return (
        <Dialog open={props.open} onClose={props.onClose} maxWidth="sm"
            TransitionComponent={Transition}
        >
            <DialogTitle>
                <Flex justifyContent="space-between" alignItems="center">
                    {props.showingId ? 'Edit Tour' : 'Create a New Tour'}
                    <IconButton onClick={props.onClose} edge="end" size="small" data-testid="close-tour-modal-button">
                        <CloseIcon />
                    </IconButton>
                </Flex>
            </DialogTitle>
            <DialogContent>
                {errorMessage && <Alert severity="warning" className={styles.modalAlert}>{errorMessage}</Alert>}

                {!isOwner && (
                    <Alert
                        severity="warning"
                        className={styles.modalAlert}
                        action={
                            <ReffieLoadingButton
                                variant="outlined"
                                color="warning"
                                size="small"
                                loading={isTransferring}
                                onClick={handleTransferOwnership}
                            >
                                    Transfer
                            </ReffieLoadingButton>
                        }
                    >
                        {`You do not own this lead. Transfer ownership of the lead from ${ownerName} to yourself before scheduling a tour.`}
                    </Alert>
                )}
                {isOwner && !hasCalendarIntegration && (
                    <Alert severity="warning" className={styles.modalAlert} data-testid="tour-no-calendar-integration-warning">
                        <Typography variant="body2">
                            You have no calendar connected, so no calendar invite will be sent. Connect Google or Outlook in{' '}
                            <Link href="/landlord/inner/settings?tab=account">Settings</Link>.
                        </Typography>
                    </Alert>
                )}
                {isOwner && hasCalendarIntegration && !leadHasEmail && (
                    <Alert severity="warning" className={styles.modalAlert} data-testid="tour-lead-no-email-warning">
                        <Typography variant="body2">
                            This lead has no email address, so no calendar invite will be sent to them.
                        </Typography>
                    </Alert>
                )}
                {isOwner && willSendInvite && (
                    <Alert severity="info" className={styles.modalAlert} data-testid="tour-invite-recipients">
                        <Typography variant="body2">
                            A calendar invite will be sent from{' '}
                            <strong>{ownerName}{senderEmail ? ` (${senderEmail})` : ''}</strong> to:
                        </Typography>
                        <ul className={styles.inviteRecipientList}>
                            {inviteRecipientEmails.map((email) => (
                                <li key={email}>{email}</li>
                            ))}
                        </ul>
                    </Alert>
                )}
                {isElise && (
                    props.showingId ? (
                        <Alert severity="warning" className={styles.modalAlert} data-testid="elise-cancel-on-website-alert">
                            <Typography variant="body2">
                                If you want to reschedule the tour, please book another tour on the property&apos;s website as well, and contact the onsite team to cancel the previous tour
                                {selectedProperty?.showing_scheduling_link && (
                                    <>
                                        {' '}
                                        <Link href={selectedProperty.showing_scheduling_link.link} target="_blank" rel="noreferrer">
                                            {selectedProperty.showing_scheduling_link.link}
                                        </Link>
                                    </>
                                )}
                            </Typography>
                        </Alert>
                    ) : (
                        <Alert severity="warning" className={styles.modalAlert} data-testid="elise-book-on-website-alert">
                            <Typography variant="body2">
                                Please book this tour on the property&apos;s website as well
                                {selectedProperty?.showing_scheduling_link && (
                                    <>
                                        :{' '}
                                        <Link href={selectedProperty.showing_scheduling_link.link} target="_blank" rel="noreferrer">
                                            {selectedProperty.showing_scheduling_link.link}
                                        </Link>
                                    </>
                                )}
                                .
                            </Typography>
                        </Alert>
                    )
                )}

                <form>
                    <FlexColumn>
                        <div>
                            <FormControl fullWidth>
                                {initSelectedLead ?
                                    <TextField variant="filled"
                                        fullWidth
                                        label="Lead Name"
                                        value={initSelectedLead.name || ''}
                                        aria-readonly
                                        inputProps={{ readOnly: true }}
                                        data-testid="lead-name-field"
                                    /> :
                                    <Autocomplete
                                        options={leadOptions}
                                        value={lead}
                                        onChange={(_event, newValue) => setLead(newValue)}
                                        renderInput={(params) => <TextField {...params} label="Lead" />}
                                        data-testid="lead-select"
                                    />
                                }
                            </FormControl>
                        </div>
                        <div>
                            <FormControl fullWidth>
                                <Autocomplete
                                    options={propertyOptions}
                                    value={property || null}
                                    onChange={(_event, newValue) => {
                                        setProperty(newValue);
                                        setEliseSelectedSlot(null);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Property"
                                            InputProps={{
                                                ...params.InputProps,
                                                style: { overflow: 'hidden' }
                                            }}
                                            inputProps={{
                                                ...params.inputProps,
                                                style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
                                            }}
                                        />
                                    )}
                                    renderOption={(_props, option) => (
                                        <li {..._props} key={option.value}>
                                            <span className="ellipsisText" style={{ display: 'block', width: '100%' }}>
                                                {option.label}
                                            </span>
                                        </li>
                                    )}
                                    data-testid="property-select"
                                />
                            </FormControl>
                        </div>
                        {isEliseIntegrationEnabled && isSelectedPropertyLoading ? (
                            <Typography variant="body2" data-testid="tour-modal-property-loading">
                                Loading property details...
                            </Typography>
                        ) : isElise ? (
                            <EliseSlotGrid
                                selectedDate={eliseSlotSelection.selectedDate}
                                onSelectDate={(newDate) => {
                                    eliseSlotSelection.setSelectedDate(newDate);
                                    setEliseSelectedSlot(null);
                                }}
                                isDateUnavailable={eliseSlotSelection.isDateUnavailable}
                                slotsForSelectedDate={eliseSlotSelection.slotsForSelectedDate}
                                selectedSlot={eliseSelectedSlot}
                                onSelectSlot={setEliseSelectedSlot}
                                isLoading={eliseSlotSelection.isAvailabilityLoading}
                                isError={eliseSlotSelection.isAvailabilityError}
                                availabilityWindowDays={eliseSlotSelection.AVAILABILITY_WINDOW_DAYS}
                                eliseTimezone={eliseSlotSelection.eliseTimezone}
                            />
                        ) : (
                            <>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <div data-testid="date-select">
                                        <DateTimePicker
                                            label="Date"
                                            value={date}
                                            onChange={(newDate) => setDate(newDate)}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    error: false,
                                                },
                                            }}
                                            minDateTime={minPickerDateTime}
                                            closeOnSelect={false}
                                        />
                                    </div>
                                </LocalizationProvider>
                                {isOwner && !isDateInPast &&(
                                    <Alert severity="info" className={styles.modalAlert} data-testid="tour-timezone-info">
                                        <Typography variant="body2">
                                            Your tour will be scheduled in{' '}
                                            <strong>{effectiveTourTimezone} ({effectiveTimezoneLabel})</strong>
                                            {isBuildingTimezone ? ', the building\'s timezone.' : ' (your timezone).'}
                                        </Typography>
                                    </Alert>
                                )}
                                {isOwner && isDateInPast && (
                                    <Alert severity="warning" className={styles.modalAlert} data-testid="tour-past-time-warning">
                                        This time is in the past. Please select a future time to schedule the tour.
                                    </Alert>
                                )}
                                {isOwner && !isDateInPast && touringHours?.is_set && isOutsideTouringHours && (
                                    <Alert severity="warning" className={styles.modalAlert} data-testid="tour-hours-warning">
                                        {`This tour is outside the building's touring hours. ${formatAvailabilitySummary(selectedDayAvailability)} (${effectiveTimezoneLabel})`}
                                    </Alert>
                                )}
                                {isOwner && !isDateInPast && exceedsConcurrency && (
                                    <Alert severity="warning" className={styles.modalAlert} data-testid="tour-concurrency-warning">
                                        This tour exceeds the number of tours allowed at the same time for this building.
                                    </Alert>
                                )}

                                <div>
                                    <FormControl fullWidth>
                                        <Checkbox
                                            value={isSelfShow}
                                            labelPlacement="start"
                                            label="Self show"
                                            onChange={setIsSelfShow}
                                        />
                                    </FormControl>
                                </div>
                            </>
                        )}
                    </FlexColumn>
                </form>

            </DialogContent>
            <DialogActions>
                {isElise ? (
                    <ReffieLoadingButton
                        color="success"
                        disabled={!isOwner || !eliseSelectedSlot}
                        loading={isSaveOrUpdateLoading}
                        onClick={handleSave}
                        data-testid="save-elise-tour-button"
                    >{props.showingId ? 'Update' : 'Create'}</ReffieLoadingButton>
                ) : (
                    <ReffieLoadingButton
                        color="success"
                        disabled={!isOwner || !lead || !property || !date || isDateInPast || (isEliseIntegrationEnabled && isSelectedPropertyLoading)}
                        loading={isSaveOrUpdateLoading}
                        onClick={handleSave}
                        data-testid="save-tour-button"
                    >{props.showingId ? 'Update' : 'Create'}</ReffieLoadingButton>
                )}
            </DialogActions>
        </Dialog>
    );
}
